var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { Table, VirtualTable } from '@devexpress/dx-react-grid-material-ui';
import Tooltip from '@material-ui/core/Tooltip';
import classNames from 'classnames';
import { groupBy, startCase } from 'lodash';
import { default as React } from 'react';
import { DataTable, ParentStyledHeaderCell, StyledTableCell } from '../Components/DataTable/DataTable';
import { MaskedTextField } from '../Components/MaskedTextField';
import { LoadingPlaceholder } from '../Components/Placeholder';
import { useMedicationSettings } from '../Config/MedicationConfig';
import { PbsRestrictionType, ProductPackGroup } from '../Contracts/MedicationModels';
import { UseAsyncStatus } from '../Hooks/useAsync';
import MedicationsNotFound from '../Images/medicationsNotFound.svg';
import { MedicationBrowserPacksDetail, PrescriberGroups } from './MedicationBrowserPacksDetail';
import styles from './MedicationBrowserPacksList.module.css';
import { isProductPackSearchResult } from '../MedicationSummary/Utilities';
import { Box } from '@material-ui/core';
var sortingConfig = [
    {
        columnName: 'poisonSchedule',
        compare: function (a, b) {
            if (a && b) {
                return a < b ? -1 : 1;
            }
            else if (a) {
                return -1;
            }
            else if (b) {
                return 1;
            }
            else {
                return 0;
            }
        }
    }
];
var sortingControlExtensions = [
    {
        columnName: 'prescriber',
        sortingEnabled: false
    }
];
var columnExtensions = [
    {
        columnName: 'product'
    },
    {
        columnName: 'maxPbsPack',
        width: 100
    },
    {
        columnName: 'rpts',
        width: 75
    },
    {
        columnName: 'schedule',
        width: 120
    },
    {
        columnName: 'prescriber',
        width: 160
    }
];
var ProductPackGroupSortOrder = [
    ProductPackGroup.Unrestricted,
    ProductPackGroup.Restricted,
    ProductPackGroup.AuthorityRequiredStreamlined,
    ProductPackGroup.AuthorityRequired,
    ProductPackGroup.Private,
    ProductPackGroup.S100
];
var columns = [
    {
        name: 'productPackGroup',
        title: 'productPackGroup',
        getCellValue: function (row) { return row.productPackGroup; }
    },
    {
        name: 'product',
        title: 'Product',
        getCellValue: function (row) { return row.name; }
    },
    {
        name: 'maxPbsPack',
        title: 'Max.. PBS pack',
        getCellValue: function (row) { var _a, _b; return (_b = (_a = row.supportedPaymentArrangement) === null || _a === void 0 ? void 0 : _a.maxPbsPacks) !== null && _b !== void 0 ? _b : 'N/A'; }
    },
    {
        name: 'rpts',
        title: 'Rpts',
        getCellValue: function (row) { var _a, _b; return (_b = (_a = row.supportedPaymentArrangement) === null || _a === void 0 ? void 0 : _a.repeats) !== null && _b !== void 0 ? _b : ''; }
    },
    {
        name: 'schedule',
        title: 'Schedule',
        getCellValue: function (row) { var _a, _b; return (_b = (_a = row.poisonSchedule) === null || _a === void 0 ? void 0 : _a.code) !== null && _b !== void 0 ? _b : ''; }
    },
    {
        name: 'prescriber',
        title: 'Prescriber',
        getCellValue: function (row) {
            var _a, _b;
            return ((_a = row.supportedPaymentArrangement) === null || _a === void 0 ? void 0 : _a.prescriberGroups) !== null &&
                ((_b = row.supportedPaymentArrangement) === null || _b === void 0 ? void 0 : _b.prescriberGroups.length) > 0 ? (React.createElement(PrescriberGroups, { row: row })) : ('');
        }
    }
];
function getPbsRestrictionTypeText(pbsRestrictionType) {
    switch (pbsRestrictionType) {
        case PbsRestrictionType.AuthorityRequired:
            return 'Authority';
        case PbsRestrictionType.AuthorityRequiredStreamlined:
            return 'Streamlined Authority';
        case PbsRestrictionType.Restricted:
            return 'Restricted';
        case PbsRestrictionType.Unrestricted:
            return 'Unrestricted';
        default:
            return '';
    }
}
var CustomStyledTableHeaderCell = function (props) {
    var parentStyleClass = props.column.name === 'maxPbsPack'
        ? styles.maxPbsPackHeaderOverride
        : props.column.name === 'product'
            ? styles.productHeaderOverride
            : styles.headerCellOverride;
    return React.createElement(ParentStyledHeaderCell, __assign({}, props, { parentStyle: parentStyleClass }));
};
var ProductCell = function (props) {
    var _a, _b;
    var productPack = props.row;
    var isPallitative = productPack.supportedPaymentArrangement.pbsProgramName === 'Palliative Care';
    return (React.createElement(StyledTableCell, __assign({}, props, { className: styles.productCellOverride }),
        React.createElement(Tooltip, { title: productPack.name, placement: 'bottom-start' },
            React.createElement("div", { className: styles.productPackNameStyle },
                React.createElement("div", null,
                    React.createElement("div", { className: classNames(styles.rowMainText, styles.wrappingCell) }, productPack.name),
                    productPack.unitOfUse && (React.createElement("div", { className: classNames(styles.rowSubText, styles.rowSubTextEllipsis) }, startCase((_b = (_a = productPack.unitOfUse) === null || _a === void 0 ? void 0 : _a.form) === null || _b === void 0 ? void 0 : _b.name)))),
                isPallitative && (React.createElement("div", null,
                    React.createElement(Box, { className: styles.palliativeStatus }, 'Palliative')))))));
};
var PoisonScheduleCell = function (props) {
    var _a, _b, _c;
    var productPack = props.row;
    var poisonSchedule = ((_a = productPack.poisonSchedule) === null || _a === void 0 ? void 0 : _a.name) === undefined || ((_b = productPack.poisonSchedule) === null || _b === void 0 ? void 0 : _b.name) === ''
        ? 'Not TGA Approved'
        : (_c = productPack.poisonSchedule) === null || _c === void 0 ? void 0 : _c.code;
    return (React.createElement(StyledTableCell, __assign({}, props, { className: styles.cellOverride }),
        React.createElement(Tooltip, { title: poisonSchedule, placement: 'bottom-start' },
            React.createElement("div", { className: classNames(styles.rowMainText, styles.wrappingCell) }, poisonSchedule))));
};
var PbsTypeCell = function (props) {
    var _a;
    var productPack = props.row;
    var toolTipText = getPbsRestrictionTypeText(productPack.supportedPaymentArrangement.restrictionType);
    return (React.createElement(StyledTableCell, __assign({}, props, { className: styles.cellOverride }),
        React.createElement(Tooltip, { title: toolTipText, placement: 'bottom-start' },
            React.createElement("div", null,
                React.createElement("div", { className: classNames(styles.rowMainText, styles.wrappingCell) }, (_a = productPack === null || productPack === void 0 ? void 0 : productPack.supportedPaymentArrangement) === null || _a === void 0 ? void 0 : _a.arrangementName),
                React.createElement("div", { className: styles.rowSubText }, toolTipText)))));
};
var CustomCellMemo = function (cellProps) {
    if (cellProps.column.name === 'product') {
        return React.createElement(ProductCell, __assign({}, cellProps));
    }
    if (cellProps.column.name === 'schedule') {
        return React.createElement(PoisonScheduleCell, __assign({}, cellProps));
    }
    return React.createElement(StyledTableCell, __assign({}, cellProps, { className: styles.cellOverride }));
};
function getRowIdFromItem(item) {
    var _a, _b;
    return ((_b = item.code +
        item.supportedPaymentArrangement.arrangementName +
        ((_a = item.supportedPaymentArrangement.pbsDetail) === null || _a === void 0 ? void 0 : _a.code)) !== null && _b !== void 0 ? _b : '');
}
function TableHeaderStub(props) {
    return React.createElement(Table.StubHeaderCell, __assign({}, props, { className: styles.medsBrowserHeaderStub }));
}
export var MedicationBrowserPacksList = function (props) {
    var _a;
    var medSettings = useMedicationSettings();
    var onRowClick = function (row) {
        if ((!!props.selectedItem && getRowIdFromItem(props.selectedItem)) !== getRowIdFromItem(row)) {
            props.onSelect(row);
            props.onSetExpandedIds([getRowIdFromItem(row)]);
        }
    };
    var onRowExpand = function (row) {
        var _a;
        if (((_a = props.expandedItemIds) === null || _a === void 0 ? void 0 : _a.length) > 0) {
            if (getRowIdFromItem(row) === props.expandedItemIds[0]) {
                props.onSetExpandedIds([]);
            }
            else {
                props.onSetExpandedIds([getRowIdFromItem(row)]);
            }
        }
        else {
            props.onSetExpandedIds([getRowIdFromItem(row)]);
        }
    };
    if (props.loadStatus === UseAsyncStatus.Init)
        return (React.createElement("div", { className: styles.placeholder },
            React.createElement("div", { className: styles.placeholderContent },
                React.createElement("img", { src: MedicationsNotFound, alt: '' }),
                React.createElement("div", { className: styles.placeholderTitleText }, "No medications listed"),
                React.createElement("div", { className: styles.placeholderText }, "Search medications by generic, brand name, PBS or TGA filters."))));
    else if (props.products != null && props.products.length > 0) {
        var productPackGrouped = groupBy(props.products, function (it) { return it.productPackGroup; });
        var sortedArrayOfGroups = Object.entries(productPackGrouped)
            .slice()
            .sort(function (a, b) {
            return ProductPackGroupSortOrder.indexOf(a[0]) -
                ProductPackGroupSortOrder.indexOf(b[0]);
        });
        var medicationsSummary = sortedArrayOfGroups.flatMap(function (_a) {
            var _b = __read(_a, 2), _ = _b[0], values = _b[1];
            return values;
        });
        return (React.createElement("div", { className: styles.dataTableContainer },
            React.createElement(DataTable, { testId: 'medication-browser', columns: columns, data: medicationsSummary, cellComponent: CustomCellMemo, headerComponent: CustomStyledTableHeaderCell, columnExtensions: columnExtensions, sortingControl: sortingControlExtensions, getRowId: function (item) {
                    if (isProductPackSearchResult(item))
                        return getRowIdFromItem(item);
                    return item.key;
                }, scrollToRowOnLoad: VirtualTable.TOP_POSITION, customSort: sortingConfig, limit: props.limit, loading: props.loadStatus === UseAsyncStatus.Reloading, hideButtonOnDisabledSortColumns: true, rowSelection: {
                    selectedIds: props.selectedItem ? [getRowIdFromItem(props.selectedItem)] : [],
                    onSelect: onRowClick
                }, rowExpansion: {
                    expandedIds: props.expandedItemIds,
                    toggleColumnWidth: 50,
                    onExpand: onRowExpand,
                    content: function (data) { return React.createElement(MedicationBrowserPacksDetail, { row: data.row }); }
                }, stubComponent: TableHeaderStub, isMedicationBrowserGrid: true })));
    }
    else if (props.loadStatus === UseAsyncStatus.Loaded)
        return (React.createElement("div", { className: styles.placeholder },
            React.createElement("div", { className: styles.placeholderContent },
                React.createElement("img", { src: MedicationsNotFound, alt: '' }),
                React.createElement("div", { className: styles.placeholderTitleText }, "No medications listed"),
                React.createElement("div", { className: styles.placeholderText }, "Search medications by generic, brand name, PBS or TGA filters."),
                props.allowFreeTextMedication && medSettings.settings.allowFreeTextMedication && (React.createElement(React.Fragment, null,
                    React.createElement("div", { className: styles.placeholderText }, "If you can't find the right medication to select, enter the new medication history item below as free-text."),
                    React.createElement(MaskedTextField, { name: 'freeTextMedicationName', label: 'Free-text medication name', testId: 'input-free-text-medication-name', partiallyFillContainer: true, value: (_a = props.freeText) !== null && _a !== void 0 ? _a : '', onChange: function (e) { return props.onEditFreeText(e); }, onClear: function () { return props.onEditFreeText(''); } }))))));
    else
        return (React.createElement("div", { className: styles.layoutContainer },
            React.createElement(LoadingPlaceholder, null)));
};
